import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircleQuestion } from 'lucide-react';
import { API_BASE } from '../../config/api';
import QuestionThread, { QUESTION_STATUS } from './QuestionThread';

const API = `${API_BASE}/api`;

export default function InstructorQuestionsPanel({ programmeId, token }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('open');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/instructor/programmes/${programmeId}/questions`, { headers: { Authorization: `Bearer ${token}` }, params: status ? { status } : {} });
      setRows(data.questions || []);
    } catch (e) { toast.error('Could not load questions'); }
  }, [programmeId, token, status]);

  useEffect(() => { load(); }, [load]);

  const onUpdated = (t) => { setSelected(t); load(); };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="instructor-questions-panel">
      <div className="lg:col-span-2 space-y-3">
        <div className="flex gap-2">
          {[['open', 'Waiting'], ['answered', 'Answered'], ['resolved', 'Resolved'], ['', 'All']].map(([v, l]) => (
            <button key={l} onClick={() => setStatus(v)} data-testid={`questions-filter-${l.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${status === v ? 'bg-teal-500 text-black' : 'border border-zinc-700 text-zinc-400 hover:text-white'}`}>{l}</button>
          ))}
        </div>
        {rows.length === 0 && (
          <div className="rounded-xl border border-dashed border-zinc-800 p-8 text-center" data-testid="questions-empty">
            <MessageCircleQuestion className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No questions here</p>
          </div>
        )}
        {rows.map((q) => (
          <button key={q.question_id} onClick={() => setSelected(q)} data-testid={`question-item-${q.question_id}`}
            className={`w-full text-left rounded-xl border p-3 transition-colors ${selected?.question_id === q.question_id ? 'border-teal-500/60 bg-teal-500/5' : 'border-zinc-800 bg-zinc-900/60 hover:border-zinc-700'}`}>
            <div className="flex justify-between gap-2">
              <p className="text-sm text-white font-medium truncate">{q.subject}</p>
              <span className={`px-2 py-0.5 rounded-full text-[10px] shrink-0 ${QUESTION_STATUS[q.status]}`}>{q.status}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1 truncate">{q.learner_name}{q.activity_title ? ` · ${q.activity_title}` : ''}</p>
          </button>
        ))}
      </div>
      <div className="lg:col-span-3 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 min-h-[240px]">
        {selected ? <QuestionThread key={selected.question_id + selected.messages.length} thread={selected} token={token} onUpdated={onUpdated} />
          : <p className="text-sm text-zinc-500">Select a question to read and reply.</p>}
      </div>
    </div>
  );
}
